import React, { useCallback, useEffect, useState } from 'react';
import DayBreakdown from './DayBreakdown';
import { api } from '../api';
import './DailyProfitPage.css';

const pad = (n) => String(n).padStart(2, '0');
const todayStr = () => { const d = new Date(); return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`; };
const rupees = (n) => (n < 0 ? '−' : '') + '₹' + Math.abs(Math.round(Number(n) || 0)).toLocaleString('en-IN');
const niceDate = (s) => new Date(`${s}T00:00:00`).toLocaleDateString('en-IN', { weekday: 'short', day: 'numeric', month: 'short' });
const niceMonth = (m) => new Date(`${m}-01T00:00:00`).toLocaleDateString('en-IN', { month: 'long', year: 'numeric' });

export default function DailyProfitPage() {
  const [month, setMonth] = useState(todayStr().slice(0, 7));
  const [data, setData] = useState(null);
  const [selected, setSelected] = useState(null);
  const [error, setError] = useState('');

  const load = useCallback(() => {
    setError('');
    api.get(`/profit/daily?month=${month}&today=${todayStr()}`)
      .then((d) => { setData(d); setSelected((cur) => (cur && d.rows.find((r) => r.date === cur.date)) || d.rows[0] || null); })
      .catch((e) => setError(e.message));
  }, [month]);
  useEffect(() => { load(); }, [load]);

  const day = selected;
  const t = data && data.totals;

  return (
    <div className="dp-root">
      <header className="dp-head">
        <div>
          <h1>Daily profit</h1>
          <p>Sales − cost of goods − expenses − EB − salary = final profit. EB comes from meter readings and salary from attendance.</p>
        </div>
        <div className="dp-head-actions">
          <input type="month" value={month} max={todayStr().slice(0, 7)} onChange={(e) => e.target.value && setMonth(e.target.value)} />
          <button type="button" className="dp-ghost" onClick={() => window.print()}>Print</button>
        </div>
      </header>

      {error && <div className="dp-error">{error}</div>}
      <div className="dp-print-title">Ice Bay x Hendy's Kitchen: daily profit, {niceMonth(month)}</div>

      {data && (
        <div className="dp-top">
          {day ? (
            <section className="dp-card dp-day">
              <h2>{niceDate(day.date)}</h2>
              <Line label={`Sales (${day.bills} bills)`} value={day.sales} />
              {day.refunds > 0 && <Line label="Refunds" value={-day.refunds} minus />}
              <Line label="Cost of goods sold" value={-day.cogs} minus />
              <Line label="Gross profit" value={day.gross_profit} strong />
              <Line label="Expenses" value={-day.expenses} minus />
              {day.expense_detail.map((x) => <Line key={x.category} label={x.category} value={-x.amount} sub />)}
              <Line label={`EB${day.eb_basis === 'bill' ? ' (from last bill)' : day.eb_basis === 'none' ? ' (no EB data yet)' : ''}`} value={-day.eb} minus />
              <Line label="Salary" value={-day.salary} minus />
              {day.salary_unmarked > 0 && <p className="dp-warn">{day.salary_unmarked} staff not marked on this day, so their salary is not counted. Mark attendance for an accurate profit.</p>}
              <div className={`dp-final ${day.net_profit < 0 ? 'is-loss' : ''}`}>
                <span>Final profit</span>
                <strong>{rupees(day.net_profit)}</strong>
              </div>
              {day.sales > 0 && <p className="dp-muted">Profit: <b>{((day.net_profit / day.sales) * 100).toFixed(1)}%</b> of sales after all costs · gross margin {((day.gross_profit / day.sales) * 100).toFixed(1)}% (after product cost only).</p>}
              {day.uncosted_sales > 0 && (
                <p className="dp-warn">{rupees(day.uncosted_sales)} of sales were items with no purchase cost recorded, so real profit is lower than shown.</p>
              )}
            </section>
          ) : <section className="dp-card dp-day"><p className="dp-muted">No days to show yet.</p></section>}

          <section className="dp-card dp-month">
            <h2>{niceMonth(month)} so far</h2>
            <div className="dp-kpis">
              <div><span>Sales</span><strong>{rupees(t.sales)}</strong></div>
              <div><span>Gross profit</span><strong>{rupees(t.gross_profit)}</strong></div>
              <div><span>Expenses + EB + salary</span><strong>{rupees(t.expenses + t.eb + t.salary)}</strong></div>
              <div className={t.net_profit < 0 ? 'is-loss' : 'is-main'}><span>Final profit</span><strong>{rupees(t.net_profit)}</strong></div>
            </div>
            {t.uncosted_sales > 0 && <p className="dp-warn">{rupees(t.uncosted_sales)} of this month's sales have no purchase cost yet. Enter those purchase bills for accurate profit.</p>}
          </section>
        </div>
      )}

      {data && (
        <div className="dp-table-wrap">
          <table className="dp-table">
            <thead>
              <tr>
                <th>Day</th><th className="num">Bills</th><th className="num">Sales</th><th className="num">Cost of goods</th>
                <th className="num">Gross profit</th><th className="num">Expenses</th><th className="num">EB</th><th className="num">Salary</th><th className="num">Final profit</th><th className="num">Profit %</th>
              </tr>
            </thead>
            <tbody>
              {data.rows.map((r) => (
                <tr key={r.date} className={`${selected && selected.date === r.date ? 'is-selected' : ''} ${r.net_profit < 0 ? 'is-loss' : ''}`} onClick={() => { setSelected(r); window.dispatchEvent(new CustomEvent('dp-day', { detail: r.date })); }}>
                  <td>{niceDate(r.date)}</td>
                  <td className="num">{r.bills}</td>
                  <td className="num">{rupees(r.sales - r.refunds)}</td>
                  <td className="num">{rupees(r.cogs)}</td>
                  <td className="num">{rupees(r.gross_profit)}</td>
                  <td className="num">{r.expenses ? rupees(r.expenses) : '—'}</td>
                  <td className="num">{r.eb ? rupees(r.eb) : '—'}</td>
                  <td className="num">{r.salary ? rupees(r.salary) : '—'}</td>
                  <td className="num"><strong>{rupees(r.net_profit)}</strong></td>
                  <td className="num">{r.sales - r.refunds > 0 ? `${((r.net_profit / (r.sales - r.refunds)) * 100).toFixed(1)}%` : "-"}</td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr>
                <td>Total</td><td className="num">{t.bills}</td><td className="num">{rupees(t.sales - t.refunds)}</td><td className="num">{rupees(t.cogs)}</td>
                <td className="num">{rupees(t.gross_profit)}</td><td className="num">{rupees(t.expenses)}</td><td className="num">{rupees(t.eb)}</td><td className="num">{rupees(t.salary)}</td>
                <td className="num"><strong>{rupees(t.net_profit)}</strong></td>
                <td className="num"><strong>{t.sales - t.refunds > 0 ? `${((t.net_profit / (t.sales - t.refunds)) * 100).toFixed(1)}%` : "-"}</strong></td>
              </tr>
            </tfoot>
          </table>
          <DayBreakdown />
          {data.rows.length === 0 && <div className="dp-empty">No days in this month yet.</div>}
        </div>
      )}
    </div>
  );
}

function Line({ label, value, minus, strong, sub }) {
  return (
    <div className={`dp-line ${strong ? 'is-strong' : ''} ${sub ? 'is-sub' : ''}`}>
      <span>{minus && !sub ? '− ' : ''}{label}</span>
      <b>{minus ? rupees(Math.abs(value)) : rupees(value)}</b>
    </div>
  );
}
